/**
 * Export composition PLAN (pure, testable).
 *
 * The exported PNG must contain exactly, in order:
 *   1. background  (documented as opaque white by default)
 *   2. paint       (all painted/erased content, composited)
 *   3. outline     (thick dark turtle outline, on top)
 *
 * It must NOT contain the toolbar, UI, or the debug overlay. The concrete pixel
 * renderer (PngExporter) consumes this plan; keeping the plan separate lets us
 * unit-test the composition order and background policy without a canvas.
 */

export type ExportLayer = 'background' | 'paper' | 'paint' | 'outline';

export type ExportBackground = 'white' | 'transparent';

export interface ExportPlan {
  background: ExportBackground;
  /** Ordered list of layers to draw, back to front. */
  layers: ExportLayer[];
  /** Layers that must never be exported (asserted by tests). */
  excluded: string[];
}

export function buildExportPlan(background: ExportBackground = 'white'): ExportPlan {
  const layers: ExportLayer[] = [];
  if (background === 'white') layers.push('background');
  // "paper" is the white turtle interior; with a white background it is
  // visually redundant but kept explicit so transparent exports still show it.
  layers.push('paper', 'paint', 'outline');
  return {
    background,
    layers,
    excluded: ['toolbar', 'ui', 'debug-overlay', 'offline-indicator'],
  };
}
